import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { UtilService } from '../../services/util-service';
import { UserService } from '../../services/user-service';
import { ConnectionService } from '../../services/connection-service';
import { AuthService } from '../../services/auth-service';
import { InsightsService } from '../../services/insights-service';
import { IInsightsDashboard } from '../../app/insights/welcome/welcome.component';

@Component({
  selector: 'app-navbar-insights',
  templateUrl: './navbar-insights.component.html',
  styleUrls: ['./navbar-insights.component.scss'],
  standalone: false
})
export class InsightsNavbarComponent implements OnInit, OnDestroy {
  @ViewChild('navbarToggler', { static: false }) navbarToggler: ElementRef;
  @ViewChild('navbarTarget', { static: false }) navbarTarget: ElementRef;

  isLoggedIn = false;
  photoSrc: string;
  levelSrc: string;
  companyName: string;
  accessibleDashboards: IInsightsDashboard[] = [];
  isDashboardDropdownOpen = false;

  photoSrcSubscription: Subscription;
  private routerSubscription: Subscription;

  @Input() logoSrc: string;

  isDevice: boolean;

  constructor(
    private userService: UserService,
    private router: Router,
    private utilService: UtilService,
    private connectionService: ConnectionService,
    private authService: AuthService,
    private insightsService: InsightsService,
  ) {
  }

  ngOnInit() {
    this.isLoggedIn = this.userService.isLoggedIn();
    this.isDevice = this.utilService.isMobileDeviceScreenWidth(screen.width);
    if (!this.isLoggedIn || !this.userService.hasProfile()) {
      return;
    }

    this.initialiseFromUserService();
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        if (event.urlAfterRedirects.includes('/insights/dashboard/') && this.accessibleDashboards.length === 0) {
          this.loadAccessibleDashboards();
        }
      });
  }

  get showDashboardSwitcher(): boolean {
    return this.accessibleDashboards.length >= 2 && this.router.url.includes('/insights/dashboard/');
  }

  get activeDashboardId(): number {
    const urlParts = this.router.url.split('/');
    return +urlParts[urlParts.length - 1];
  }

  get activeDashboardName(): string {
    const active = this.accessibleDashboards.find(d => d.dashboardId === this.activeDashboardId);
    return active ? active.name : 'Dashboards';
  }

  get otherDashboards(): IInsightsDashboard[] {
    return this.accessibleDashboards.filter(d => d.dashboardId !== this.activeDashboardId);
  }

  private loadAccessibleDashboards() {
    this.insightsService.getMyDashboards().subscribe({
      next: (data) => {
        this.accessibleDashboards = data.filter(d => d.hasAccess);
      },
    });
  }

  onDashboardSelect(dashboard: IInsightsDashboard) {
    this.isDashboardDropdownOpen = false;
    this.router.navigate(['/insights/dashboard', dashboard.dashboardId]);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.dashboard-switcher')) {
      this.isDashboardDropdownOpen = false;
    }
  }

  isConnectionLoaded(): Observable<boolean> {
    return this.connectionService.isCompleted;
  }

  ngOnDestroy() {
    if (this.photoSrcSubscription) {
      this.photoSrcSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  onLogoSelected() {
    const url = '/home';
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([url]);
    });
    this.hideNavbarDropdownMenu();
  }

  private hideNavbarDropdownMenu() {
    if (!this.isShowingNavbarToggler()) {
      return;
    }
    this.navbarToggler.nativeElement.click();
  }

  private isShowingNavbarToggler(): boolean {
    if (!this.navbarToggler) {
      return false;
    }

    const domElement = this.navbarToggler.nativeElement;
    return (domElement.offsetHeight > 0 && domElement.offsetWidth > 0);
  }

  private initialiseFromUserService() {

    this.companyName = this.userService.company
      ? this.userService.company.name
      : this.userService.getPlatformTitle();
    this.userService.loadCompanyTheming();
    this.userService.refreshCurrentProfileSrc();
    this.logoSrc = this.userService.getLogoSrc();
    this.photoSrc = this.userService.getMyPhotoSrc();
    this.levelSrc = this.userService.getMyLevelSrc();

    this.photoSrcSubscription = this.userService.profilePic$
      .subscribe(src => this.photoSrc = src);
  }

  onMainMenuClick(event: Event, page) {
    if (event && !this.utilService.isMobileDeviceScreenWidth(screen.width)) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  onLoginClick() {
    this.authService.handleLoginProcess();
  }
}
